import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea
} from 'recharts';

import { TICK_STYLE, TOOLTIP_WRAPPER_STYLE, CHART_COLORS } from './constants';
import { ModelUsageTooltip } from './tooltips';
import { OverlayLegend } from './OverlayLegend';
import type { ModelDistributionPoint, ChartReferenceArea } from './types';

interface ModelDistributionChartProps {
  data: ModelDistributionPoint[];
  uniqueModels: string[];
  showOverlays: boolean;
  referenceAreas: ChartReferenceArea[];
  formatTimestamp: (value: number | string) => string;
  onToggleOverlays: () => void;
}

export const ModelDistributionChart: React.FC<ModelDistributionChartProps> = ({
  data,
  uniqueModels,
  showOverlays,
  referenceAreas,
  formatTimestamp,
  onToggleOverlays
}) => (
  <div className="p-2 sm:p-3">
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        {showOverlays && referenceAreas.map((area) => (
          <ReferenceArea
            key={`${area.type}-${area.start}`}
            x1={area.start}
            x2={area.end}
            fill={area.type === 'holiday' ? '#fef3c7' : '#f3f4f6'}
            fillOpacity={0.5}
            stroke="none"
          />
        ))}
        <XAxis
          dataKey="date"
          tick={TICK_STYLE}
          tickFormatter={formatTimestamp}
          stroke="#6b7280"
          interval="preserveEnd"
        />
        <YAxis
          tick={TICK_STYLE}
          stroke="#6b7280"
          domain={[0, 100]}
          ticks={[0, 20, 40, 60, 80, 100]}
        />
        <Tooltip
          content={<ModelUsageTooltip />}
          wrapperStyle={TOOLTIP_WRAPPER_STYLE}
        />
        <Legend />
        {uniqueModels.map((model, index) => (
          <Area
            key={model}
            type="monotone"
            dataKey={model}
            stackId="1"
            stroke={CHART_COLORS[index % CHART_COLORS.length]}
            fill={CHART_COLORS[index % CHART_COLORS.length]}
            name={model}
            connectNulls={true}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
    <OverlayLegend showOverlays={showOverlays} onToggle={onToggleOverlays} />
  </div>
);
